import { useEffect, useState } from "react";
import "../inward-styles.css";
import Icon from "@mdi/react";
import { mdiPackageVariant } from "@mdi/js";
import { useHistory, useLocation } from "react-router-dom";
import RegularSTForm from "./create_st_pages/RegularSTForm";
import ReturnableSTForm from "./create_st_pages/ReturnableSTForm";
import ReplacementSTForm from "./create_st_pages/ReplacementSTForm";
import FoCSTForm from "./create_st_pages/FoCSTForm";
import ReverseSTForm from "./create_st_pages/ReverseSTForm";
import { useWarehouseViewOnly } from "../hooks/useWarehouseViewOnly";
import { urls } from "../../../../utils/urls";

type DCType = "REGULAR" | "RETURNABLE" | "REPLACEMENT" | "FOC" | "REVERSE_DC";

const DC_TYPES: DCType[] = [
  "REGULAR",
  "RETURNABLE",
  "REPLACEMENT",
  "FOC",
  "REVERSE_DC",
];

export default function CreateStockTransferPage(): JSX.Element {
  const isWarehouseViewOnly = useWarehouseViewOnly();
  const history = useHistory();
  const location = useLocation();

  // Persist the active DC type in the URL (lowercase) so a refresh restores
  // the right form.
  const urlType = (
    new URLSearchParams(location.search).get("type") ?? ""
  ).toUpperCase() as DCType;
  const [dcType, setDcType] = useState<DCType>(
    DC_TYPES.includes(urlType) ? urlType : "REGULAR",
  );

  const changeDcType = (next: DCType) => {
    setDcType(next);
    const params = new URLSearchParams(location.search);
    params.set("type", next.toLowerCase());
    // Subtype belongs to the previous form; let the new form seed its own.
    params.delete("subtype");
    history.replace({ search: params.toString() });
  };

  useEffect(() => {
    if (isWarehouseViewOnly) {
      history.replace(urls.warehouse_panel + "/stock-transfers");
    }
  }, [history, isWarehouseViewOnly]);

  if (isWarehouseViewOnly) return <></>;

  const renderForm = () => {
    switch (dcType) {
      case "REGULAR":
        return <RegularSTForm />;
      case "RETURNABLE":
        return <ReturnableSTForm />;
      case "REPLACEMENT":
        return <ReplacementSTForm />;
      case "FOC":
        return <FoCSTForm />;
      case "REVERSE_DC":
        return <ReverseSTForm />;
      default:
        return null;
    }
  };

  return (
    <div
      id="warehouse-stock-transfer-create-page"
      className="wh-mobile-shrink min-h-screen bg-gray-50 w-full mx-auto"
    >
      <div className=" bg-white flex flex-col p-2 sm:p-6 lg:p-4">
        {/* HEADER */}
        <div className="border-b px-2 pb-2 flex items-center gap-2">
          <Icon path={mdiPackageVariant} size={1} />
          <h1 className="text-xl font-semibold">Create Stock Transfer</h1>
        </div>

        {/*TOGGLE*/}
        <div className="p-6 space-y-6">
          {/* DC TYPE TOGGLE */}
          <div className="space-y-2">
            <label className="text-sm font-medium">DC Type</label>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
              {[
                {
                  key: "REGULAR",
                  label: "Regular",
                  desc: "Standard stock transfer",
                },
                {
                  key: "RETURNABLE",
                  label: "Returnable",
                  desc: "Expected to return",
                },
                {
                  key: "REPLACEMENT",
                  label: "Replacement",
                  desc: "Device replacement",
                },
                {
                  key: "FOC",
                  label: "FoC",
                  desc: "Customer free of cost",
                },
                {
                  key: "REVERSE_DC",
                  label: "Reverse DC",
                  desc: "Reverse pickup into warehouse",
                },
              ].map((type) => {
                const selected = dcType === type.key;

                return (
                  <button
                    key={type.key}
                    id={`warehouse-stock-transfer-create-dc-type-${type.key.toLowerCase()}`}
                    type="button"
                    onClick={() => changeDcType(type.key as DCType)}
                    className={`p-3 rounded-lg border-2 text-left transition-all
            ${
              selected
                ? "border-blue-600 bg-blue-50"
                : "border-gray-200 hover:border-blue-400 hover:bg-gray-50"
            }`}
                  >
                    <div className="font-medium">{type.label}</div>
                    <p className="text-xs text-gray-500">{type.desc}</p>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* BODY */}
        {renderForm()}
      </div>
    </div>
  );
}
